import * as EventTracker from '@src/analytics/EventTracker';
import { useEnhancementPublishGate } from '@src/hooks/useEnhancementPublishGate';
import { usePublishLanguage } from '@src/hooks/usePublishLanguage';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { pickHighestAvailableResolutionLevel } from '@src/utility/videoResolution';
import { get } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { Tooltip } from 'react-tooltip';

import { VideoResolution } from '../../types.ts/video';
import SplitButton from '../common/SplitButton';
import { TOOLTIP_STYLE } from '../common/tooltipStyles';

import type { SplitButtonItem } from '../common/SplitButton';

type Props = {};

export const CreateButton: React.FC<Props> = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const { renderUsingChunks } = useFlagsCombination(userIdentity.currentRole);
  const publishLanguage = usePublishLanguage();
  // PLAT-11227: publishing never silently ships raw audio as enhanced —
  // disabled while corrected renditions are pending; consent-downgrade
  // prompt after a terminal enhancement failure.
  const enhancementGate = useEnhancementPublishGate();

  // Gate "Publishing..." on whether the CURRENTLY-loaded video is the one
  // rendering, not the global render status. Each aspect ratio is a separate
  // video the backend renders concurrently (tracked in `renderingVideoIds`),
  // so a render in flight for one AR must not block publishing another after
  // the user switches aspect ratios. `get()` keeps the array read observable.
  const currentVideoId = videoCreator.currentVideo?.id;
  const isCurrentVideoRendering =
    !!currentVideoId &&
    get(videoCreator, 'renderingVideoIds').includes(currentVideoId);

  const busyLabel = isCurrentVideoRendering
    ? 'Publishing...'
    : videoCreator.renderQueueing
      ? 'Queueing...'
      : undefined;

  const publish = (res: VideoResolution) =>
    enhancementGate.guardPublish(() => {
      EventTracker.trackClipPublished({
        clip_id: videoCreator.currentVideo?.id,
        story_id: videoCreator.story?.id,
        source_platform: 'creator-studio',
        resolution: res,
      });
      videoCreator.publishVideo(res, renderUsingChunks, publishLanguage);
    })();

  const originalRes = videoCreator.story?.originalVideo
    ? Math.min(
        videoCreator.story.originalVideo.width,
        videoCreator.story.originalVideo.height,
      )
    : undefined;
  const defaultRes: VideoResolution = pickHighestAvailableResolutionLevel(
    videoCreator.story?.originalVideo?.video,
  );

  const resItem = (
    key: string,
    label: string,
    res: VideoResolution,
    minP: number,
  ): SplitButtonItem => ({
    key,
    label,
    onSelect: () => publish(res),
    disabled: !originalRes || minP > originalRes,
    note: defaultRes === res ? 'Default' : undefined,
  });

  const items: SplitButtonItem[] = [
    resItem('low', 'Low (270p)', VideoResolution.Low, 270),
    resItem('medium', 'Medium (480p)', VideoResolution.Medium, 480),
    resItem('high', 'High (720p)', VideoResolution.High, 720),
    resItem('hd', 'HD (1080p)', VideoResolution.HD, 1080),
    ...(videoCreator.story?.originalVideo
      ? [
          {
            key: 'original',
            label: `Original (${originalRes}p)`,
            onSelect: () => publish(VideoResolution.Original),
            disabled: !originalRes,
          },
        ]
      : []),
  ];

  return (
    <span
      data-tooltip-id="publish-enhancement-gate-tooltip"
      data-tooltip-content={enhancementGate.tooltip}
    >
      <SplitButton
        label="Publish"
        onClick={() => publish(defaultRes)}
        items={items}
        disabled={
          !videoCreator.isVideoCreatorReady || enhancementGate.isBlocked
        }
        busy={!!busyLabel}
        busyLabel={busyLabel}
        destructive={videoCreator.renderingStatus === 'error'}
        menuAriaLabel="Publish resolution options"
      />
      {/* Always mounted so react-tooltip is initialized before the first
          hover; it only shows while the wrapper carries data-tooltip-content
          (i.e. while the gate is blocked). */}
      <Tooltip
        id="publish-enhancement-gate-tooltip"
        style={TOOLTIP_STYLE}
        place="bottom"
      />
    </span>
  );
});
